/*
  AMAVO — six-question skin protocol.
  Shopify supplies current product data; this script only maps the approved
  taxonomy (skin state, need, routine step and AM/PM use) to a routine.
*/

(() => {
  'use strict';

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[character]));

  class AmvQuiz extends HTMLElement {
    connectedCallback() {
      this.questions = Array.from(this.querySelectorAll('[data-amv-quiz-question]'));
      if (this.questions.length !== 6) return;

      this.results = this.querySelector('[data-amv-quiz-results]');
      this.progress = this.querySelector('[data-amv-quiz-progress]');
      this.step = this.querySelector('[data-amv-quiz-step]');
      this.topic = this.querySelector('[data-amv-quiz-topic]');
      this.fill = this.querySelector('[data-amv-quiz-fill]');
      this.index = 0;
      this.answers = {};

      try {
        this.i18n = JSON.parse(this.querySelector('[data-amv-quiz-i18n]')?.textContent || '{}');
      } catch (_) {
        this.i18n = {};
      }

      try {
        this.catalog = JSON.parse(this.querySelector('[data-amv-quiz-catalog]')?.textContent || '[]');
      } catch (_) {
        this.catalog = [];
      }
      this.products = new Map(this.catalog.map((product) => [product.handle, product]));

      this.questions.forEach((question, index) => {
        const inputs = Array.from(question.querySelectorAll('[data-amv-quiz-input]'));
        const next = question.querySelector('[data-amv-quiz-next]');
        question.querySelector('[data-amv-quiz-back]')?.addEventListener('click', () => this.go(index - 1));
        next?.addEventListener('click', () => this.go(index + 1));
        inputs.forEach((input) => input.addEventListener('change', () => this.record(question, inputs, input, next)));
      });
      this.querySelector('[data-amv-quiz-restart]')?.addEventListener('click', () => this.restart());
      this.show(0);
    }

    t(key, replacements = {}) {
      let value = this.i18n[key] || key;
      Object.entries(replacements).forEach(([name, replacement]) => {
        value = value.replaceAll('{{ ' + name + ' }}', replacement);
      });
      return value;
    }

    price(cents) {
      const locale = this.i18n.locale === 'en' ? 'en-GB' : this.i18n.locale === 'it' ? 'it-IT' : 'ro-RO';
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format((Number(cents) || 0) / 100) + ' lei';
    }

    record(question, inputs, changed, next) {
      const max = Number(question.dataset.max) || 1;
      if (max > 1 && inputs.filter((input) => input.checked).length > max) changed.checked = false;
      const selected = inputs.filter((input) => input.checked).map((input) => input.value);
      this.answers[question.dataset.questionKey] = selected;
      if (next) next.disabled = selected.length === 0;
    }

    show(index) {
      this.questions.forEach((question, current) => { question.hidden = current !== index; });
      this.index = index;
      const question = this.questions[index];
      const topic = question.dataset.topic || (question.querySelector('.amv-eyebrow')?.textContent || '').split('·').pop().trim();
      if (this.step) this.step.textContent = this.t('question_progress', { current: String(index + 1), total: String(this.questions.length) });
      if (this.topic) this.topic.textContent = topic;
      // The bar counts the question you are on, not the ones behind you:
      // an empty track on the first screen reads as a broken widget.
      if (this.fill) this.fill.style.width = ((index + 1) / this.questions.length) * 100 + '%';
    }

    go(index) {
      if (index < 0) return;
      if (index >= this.questions.length) {
        this.finish();
        return;
      }
      this.show(index);
      const legend = this.questions[index].querySelector('.amv-quiz__legend');
      if (legend) {
        legend.tabIndex = -1;
        legend.focus({ preventScroll: true });
      }
      this.questions[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    pickProduct(step, ten, needs, preferred = [], avoid = []) {
      const stepTag = 'pas_' + step;
      const skinTag = 'ten_' + ten;
      const candidates = this.catalog.filter((product) => {
        const tags = product.tags || [];
        return tags.some((tag) => tag === stepTag || tag.startsWith(stepTag + '-')) && !tags.includes('tip_set');
      });
      const score = (product) => {
        const tags = product.tags || [];
        let value = tags.includes(skinTag) ? 8 : 0;
        for (const need of needs) if (tags.includes('nevoie_' + need)) value += 4;
        for (const tag of preferred) if (tags.includes(tag)) value += 6;
        for (const tag of avoid) if (tags.includes(tag)) value -= 30;
        if (tags.includes('fara_parfum')) value += 1;
        return value;
      };
      return candidates.sort((a, b) => score(b) - score(a))[0] || null;
    }

    pickSet(family, time, needs) {
      const familyTag = 'gama_' + family;
      const sets = this.catalog.filter((product) => {
        const tags = product.tags || [];
        return tags.includes('tip_set') && (tags.includes(familyTag) || family === 'seturi');
      });
      const score = (product) => {
        const tags = product.tags || [];
        let value = 0;
        if (time === 'scurt' && tags.includes('set_3')) value += 8;
        if (time !== 'scurt' && tags.includes('set_full')) value += 8;
        for (const need of needs) if (tags.includes('nevoie_' + need)) value += 4;
        if (tags.includes('set_revizuit')) value += 2;
        return value;
      };
      return sets.sort((a, b) => score(b) - score(a))[0] || null;
    }

    buildRoutine() {
      const ten = this.answers.ten?.[0] || 'normal';
      const reactiv = this.answers.reactiv?.[0] || 'deloc';
      const needs = this.answers.nevoie || [];
      const time = this.answers.timp?.[0] || 'mediu';
      const sun = this.answers.soare?.[0] || 'mediu';
      const eyes = this.answers.ochi?.[0] === 'da';
      const barrierFirst = reactiv === 'mult' || reactiv === 'post' || needs.includes('bariera');
      const bright = needs.includes('luminozitate');
      const lift = needs.includes('fermitate');
      const dry = ten === 'uscat' || ten === 'deshidratat';
      const oily = ten === 'gras' || ten === 'mixt';
      const avoid = reactiv === 'mult' || reactiv === 'post' ? ['contine_parfum', 'atentie_sarcina'] : [];
      const routine = [];
      const add = (step, label, preferred, note) => {
        const product = this.pickProduct(step, ten, needs, preferred, avoid);
        if (product) routine.push({ step, label, product, note });
      };

      add('01', this.t('step_cleanse'), dry || barrierFirst ? ['nevoie_bariera', 'nevoie_calmare'] : ['nevoie_pori'],
        this.t(dry || barrierFirst ? 'note_cleanse_gentle' : 'note_cleanse_sebum'));
      if (time !== 'scurt') add('02', this.t('step_tone'), lift || dry ? ['nevoie_fermitate', 'activ_peptide'] : ['nevoie_luminozitate'], this.t('note_tone'));

      if (barrierFirst) add('03', this.t('step_serum_ampoule'), ['nevoie_bariera', 'nevoie_calmare', 'tip_ser'], this.t(reactiv === 'post' ? 'note_recovery_priority' : 'note_barrier_support'));
      else if (bright) add('03', this.t('step_serum'), ['nevoie_luminozitate', 'activ_vitamina-c'], this.t('note_brightening'));
      else if (lift) add('03', this.t('step_essence'), ['nevoie_fermitate', 'tip_esenta'], this.t('note_firming'));
      else add('03', this.t('step_serum_ampoule'), ['nevoie_hidratare', 'nevoie_bariera'], this.t('note_hydration'));

      if (time === 'lung' && !barrierFirst && oily) add('04', this.t('step_emulsion'), ['nevoie_luminozitate', 'nevoie_hidratare'], this.t('note_emulsion'));
      add('05', this.t('step_cream'),
        reactiv === 'post' ? ['uz_post-procedura', 'nevoie_calmare'] : barrierFirst ? ['nevoie_bariera', 'nevoie_calmare'] : lift ? ['nevoie_fermitate', 'gama_stemcell'] : bright ? ['nevoie_luminozitate', 'gama_derma-brightening'] : ['nevoie_hidratare', 'nevoie_bariera'],
        this.t(reactiv === 'post' ? 'note_cream_recovery' : barrierFirst ? 'note_cream_barrier' : lift ? 'note_cream_firming' : bright ? 'note_cream_brightening' : 'note_cream_hydration'));
      if (eyes && time !== 'scurt') add('05', this.t('step_eye'), ['nevoie_ochi', 'tip_ochi'], this.t('note_eye'));
      if (time === 'lung') add('06', this.t('step_mask'), ['nevoie_calmare', 'nevoie_hidratare'], this.t('note_mask'));
      if (sun !== 'putin' || bright) add('07', this.t('step_spf'), sun === 'mult' ? ['uz_reaplicare', 'format_stick'] : ['nevoie_protectie', 'tip_spf'], this.t(sun === 'mult' ? 'note_spf_reapply' : 'note_spf_daily'));

      const family = barrierFirst ? 'microbiome' : lift ? 'stemcell' : bright ? 'derma-brightening' : 'microbiome';
      return { ten, reactiv, needs, time, eyes, barrierFirst, bright, lift, routine, set: this.pickSet(family, time, needs) };
    }

    finish() {
      const result = this.buildRoutine();
      this.render(result);
      this.questions.forEach((question) => { question.hidden = true; });
      if (this.progress) this.progress.hidden = true;
      if (this.results) {
        this.results.hidden = false;
        this.results.focus({ preventScroll: true });
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    render(result) {
      const { ten, reactiv, needs, time, eyes, barrierFirst, bright, lift, routine, set } = result;
      const verdict = reactiv === 'post' ? 'recovery' : barrierFirst ? 'barrier' : lift && bright ? 'firm_bright' : lift ? 'firm' : bright ? 'bright' : 'balanced';
      const title = this.t('verdict_' + verdict + '_title');
      const description = this.t('verdict_' + verdict + '_text');

      this.querySelector('[data-amv-quiz-verdict-title]').textContent = title;
      this.querySelector('[data-amv-quiz-verdict-text]').textContent = description;
      const tags = [this.t('skin_prefix') + ' ' + this.t('skin_' + ten)].concat(needs.map((need) => this.t('need_' + need)).filter((label) => !label.startsWith('need_')));
      if (reactiv === 'mult') tags.push(this.t('tag_sensitive'));
      if (reactiv === 'post') tags.push(this.t('tag_post_procedure'));
      if (eyes) tags.push(this.t('tag_eye'));
      this.querySelector('[data-amv-quiz-tags]').innerHTML = tags.map((tag) => '<span>' + escapeHtml(tag) + '</span>').join('');

      this.querySelector('[data-amv-quiz-products]').innerHTML = routine.map(({ step, label, product, note }) => {
        const image = product.image ? '<img src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.image_alt) + '" width="180" height="180" loading="lazy">' : '';
        const status = product.available ? this.price(product.price) : this.t('unavailable');
        return '<article class="amv-quiz__product' + (product.available ? '' : ' is-unavailable') + '">' + image + '<div class="amv-quiz__product-body"><span class="amv-eyebrow">' + escapeHtml(step) + ' · ' + escapeHtml(label) + '</span><h4><a href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a></h4><p>' + escapeHtml(note) + '</p><span class="amv-quiz__price">' + status + '</span></div></article>';
      }).join('');

      const available = routine.map(({ product }) => product).filter((product) => product.available && product.variant_id);
      const total = available.reduce((sum, product) => sum + Number(product.price || 0), 0);
      this.querySelector('[data-amv-quiz-total]').textContent = this.t('products_count', { count: String(available.length), total: this.price(total) });
      this.querySelector('[data-amv-quiz-cart-items]').innerHTML = available.map((product, index) => '<input type="hidden" name="items[' + index + '][id]" value="' + escapeHtml(product.variant_id) + '"><input type="hidden" name="items[' + index + '][quantity]" value="1">').join('');
      this.querySelector('[data-amv-quiz-add]').disabled = available.length === 0;

      const warnings = [];
      if (reactiv === 'post') warnings.push(this.t('warning_post_procedure'));
      if (reactiv === 'mult') warnings.push(this.t('warning_sensitive'));
      if (bright) warnings.push(this.t('warning_spf'));
      if (routine.some(({ product }) => (product.tags || []).includes('activ_retinol'))) warnings.push(this.t('warning_retinol'));
      const warningBox = this.querySelector('[data-amv-quiz-warnings]');
      warningBox.hidden = warnings.length === 0;
      warningBox.innerHTML = warnings.length ? '<h3>' + escapeHtml(this.t('warnings_title')) + '</h3>' + warnings.map((warning) => '<p>' + escapeHtml(warning) + '</p>').join('') : '';

      const setBox = this.querySelector('[data-amv-quiz-set]');
      setBox.hidden = !set;
      setBox.innerHTML = set ? '<h3>' + escapeHtml(this.t('set_title')) + '</h3><p><a href="' + escapeHtml(set.url) + '">' + escapeHtml(set.title) + '</a> ' + escapeHtml(this.t('set_text')) + ' ' + escapeHtml(this.price(set.price)) + '.</p>' : '';
    }

    restart() {
      this.answers = {};
      this.querySelectorAll('[data-amv-quiz-input]').forEach((input) => { input.checked = false; });
      this.querySelectorAll('[data-amv-quiz-next]').forEach((button) => { button.disabled = true; });
      if (this.results) this.results.hidden = true;
      if (this.progress) this.progress.hidden = false;
      this.show(0);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (!customElements.get('amv-quiz')) customElements.define('amv-quiz', AmvQuiz);
})();
